import type {
  MovementDocType,
  MovementType,
  RequisitionStatus,
  SurveyStatus,
  SupplyReportStatus,
  Unit,
} from '@eombor/contracts';
import { t } from './index.js';

/**
 * SANOQ QIYMATLARINING TARJIMASI.
 *
 * Shartnoma paketida (`@eombor/contracts`) har sanoq uchun yorliq bor
 * (`requisitionStatusLabel`, `unitLabel`, …), lekin ular O'ZBEKCHA qotib
 * yozilgan va shunday bo'lishi ham kerak: shartnomani API ham o'qiydi va
 * u yerda xato xabari, PDF sarlavhasi va audit yozuvi tildan qat'i nazar
 * bir xil bo'lishi lozim.
 *
 * Ekran esa boshqa gap. Rus tiliga o'tilganda "Yuborilgan" va "Sklad
 * qabul qildi" o'zbekcha qolib ketardi va sahifa ikki tilda gaplashardi.
 * Shuning uchun EKRAN yorliqlari shu yerda, ilovaning o'z lug'atidan
 * olinadi.
 *
 * Kalitlar `status.requisition.SUBMITTED` shaklida: sanoq qiymati
 * kalitning O'ZI bo'lgani uchun yangi holat qo'shilganda TypeScript
 * `Record` ni to'ldirishni talab qiladi va unutilgan tarjima lug'at
 * tekshiruvida (`i18n.test.ts`) ko'rinadi.
 */

export const requisitionStatusText = (status: RequisitionStatus): string =>
  t(`status.requisition.${status}`);

/**
 * Hali BO'LMAGAN bosqichning nomi — kutish shaklida.
 *
 * Yo'l chizmasidagi kelajak bosqich o'tgan zamonda yozilsa, o'qigan odam
 * uni bo'lib o'tgan deb tushunadi: «Korxona tasdiqladi» yozuvi ostida
 * «kutilmoqda» belgisi turgani ziddiyat bo'lib ko'rinardi. Kutilayotgan
 * qadam «Korxona tasdiqlashi kutilmoqda» bo'lib yoziladi va shu holda
 * belgi ham, matn ham bir narsani aytadi.
 */
export const requisitionPendingText = (status: RequisitionStatus): string =>
  t(`status.requisition.pending.${status}`);

export const surveyStatusText = (status: SurveyStatus): string => t(`status.survey.${status}`);

export const supplyStatusText = (status: SupplyReportStatus): string =>
  t(`status.supply.${status}`);

export const unitText = (unit: Unit): string => t(`unit.${unit}`);

export const movementTypeText = (type: MovementType): string => t(`movement.type.${type}`);

export const movementDocTypeText = (docType: MovementDocType): string =>
  t(`movement.doc.${docType}`);

/*
 * 1C HISOBOTINING SARLAVHALARI.
 *
 * Fayl tepasidagi ikki qator jadval sarlavhasidan YUQORIDA turadi va
 * parser ularni xom holicha o'qib oladi: «За Август 2026 г.» va
 * «По складу 01-Карши МТУ Асосий омбор». Bazaga ham shu ko'rinishda
 * yoziladi — rasmiy qog'ozda aynan shunday chiqishi kerak.
 *
 * Ekran esa boshqa gap: interfeys o'zbekcha tanlangan bo'lsa-yu, ichida
 * «За Август» tursa, sahifa ikki tilda gaplashadi. Shuning uchun bu
 * ikkisi KO'RSATISHDAN oldin tozalanadi — bazadagi qiymat tegilmaydi.
 *
 * Tanilmagan matn O'Z HOLICHA qaytadi. Taxmin qilinmaydi: hisobot boshqa
 * shaklda kelsa, uni buzib ko'rsatgandan ko'ra xom holicha ko'rsatgan
 * yaxshiroq.
 */

/** «августа» ham, «Август» ham uchraydi — ikkala shakl ham tanilsin. */
const RU_MONTHS: Readonly<Record<string, number>> = {
  январь: 1, января: 1,
  февраль: 2, февраля: 2,
  март: 3, марта: 3,
  апрель: 4, апреля: 4,
  май: 5, мая: 5,
  июнь: 6, июня: 6,
  июль: 7, июля: 7,
  август: 8, августа: 8,
  сентябрь: 9, сентября: 9,
  октябрь: 10, октября: 10,
  ноябрь: 11, ноября: 11,
  декабрь: 12, декабря: 12,
};

/**
 * «За Август 2026 г.» → «Avgust 2026» (joriy tilda).
 *
 * Oy nomi lug'atdan olinadi (`month.8`), ya'ni rus tiliga o'tilganda yana
 * «Август» bo'lib qaytadi — matn TARJIMA qilinadi, almashtirilmaydi.
 */
export const supplyPeriodText = (raw: string | null): string | null => {
  if (raw === null) return null;

  const cleaned = raw
    .replace(/^\s*за\s+/iu, '')
    .replace(/\s*г\.?\s*$/iu, '')
    .trim();
  if (cleaned === '') return null;

  const match = /^(\p{L}+)\s+(\d{4})$/u.exec(cleaned);
  if (match === null) return cleaned;

  const [, name, year] = match;
  if (name === undefined || year === undefined) return cleaned;

  const month = RU_MONTHS[name.toLocaleLowerCase('ru')];
  return month === undefined ? cleaned : `${t(`month.${month}`)} ${year}`;
};

/**
 * «По складу 01-Карши МТУ Асосий омбор» → «01-Карши МТУ Асосий омбор».
 *
 * FAQAT ruscha old qo'shimcha kesiladi. Qolgani — ombor nomi, ya'ni
 * bazadagi atoqli ot: uni tarjima qilib bo'lmaydi va qilinmaydi ham.
 * Yorlig'i («Ombor») esa lug'atdan keladi va tilga ergashadi.
 */
export const supplySourceText = (raw: string | null): string | null => {
  if (raw === null) return null;
  const cleaned = raw.replace(/^\s*по\s+складу\s+/iu, '').trim();
  return cleaned === '' ? null : cleaned;
};
